import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { APP_CONFIG, AppVersion } from '@application/config';
import {
  withoutForbiddenElevation,
  withUtcDateFields,
} from '@application/data-access';
import { Observable } from 'rxjs';
import {
  TComment,
  TProfile,
  TPhone,
  TResponsible,
  TAddress,
  IPersonWithIncludesParams,
  EPersonIncludeType,
  TCommentToggleRead,
  TTimelineEvent,
  TGraduationList,
  TGraduation,
  TPersonIntegration,
  TPaguelaAccount,
  TActivity,
  TPersonLookupSummary,
  TSchoolEnrollment,
} from '../entities/profile.model';
import { TOwnerSettingsByTenant } from '../entities/owner-settings-by-tenant.model';
import { TAccessLog } from '../profile.store';
import {
  ELogicalDirectionEnum,
  TCategory,
  TCategoryHistory,
} from '@settings/data-access';
import {
  CreditAssessmentPF,
  CreditAssessmentPFAll,
  CreditAssessmentPJ,
  CreditAssessmentPJAll,
} from '../entities/credit-assessment.model';
import { TPersonProduct } from '@consumption/data-access';
import { TPersonSearch } from '@shared/components/navbar/data-access';
import { EPaguelaSituation } from '@financial/data-access';
import { DateTime } from 'luxon';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;

  public getProfile(id: string): Observable<TProfile> {
    return this.http.get<TProfile>(`${this.apiUrl.personService}/Person`, {
      params: { id },
      withCredentials: true,
    });
  }

  public getPersonsLookupByIds(
    personIds: string[]
  ): Observable<{ '@odata.context': string; value: TPersonLookupSummary[] }> {
    const filter = [...new Set(personIds.filter(Boolean))]
      .map((personId) => `PersonId eq ${personId}`)
      .join(' or ');

    return this.http.get<{
      '@odata.context': string;
      value: TPersonLookupSummary[];
    }>(`${this.apiUrl.odata_person_service}/PersonsList`, {
      params: {
        $filter: filter,
        $orderby: 'FullDisplayName asc',
        $select: 'PersonId,FullDisplayName,FullName,Email,Phones,IsActive',
      },
      withCredentials: true,
    });
  }

  /**
   * Owner settings of a client that is itself a tenant. The body is null on a
   * 204, which is how the account service says the tenant has no settings.
   */
  public getOwnerSettingsByTenantIdAsync(
    tenantId: string
  ): Observable<TOwnerSettingsByTenant | null> {
    return this.http.get<TOwnerSettingsByTenant | null>(
      `${this.apiUrl.account_service}/OwnerSettings/GetOwnerSettingsByTenantIdAsync`,
      {
        params: { tenantId },
        withCredentials: true,
      }
    );
  }

  public updateAppVersionAsync(
    tenantId: string,
    appVersion: string
  ): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl.account_service}/OwnerSettings/UpdateAppVersionAsync`,
      null,
      {
        params: { tenantId, appVersion },
        withCredentials: true,
      }
    );
  }

  /**
   * Switches the tenant's access control on the legacy app between the legacy
   * one (`false`) and the new integration (`true`).
   */
  public updateActuarLegacyUseNewAccessControl(
    tenantId: string,
    useNewAccessControl: boolean
  ): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl.account_service}/OwnerSettings/UpdateActuarLegacyUseNewAccessControl`,
      null,
      {
        params: { tenantId, useNewAccessControl: String(useNewAccessControl) },
        withCredentials: true,
      }
    );
  }

  /**
   * Where a tenant's own subscription with the platform lives. Only a platform
   * team token may write it (403 otherwise), so it is only called while that
   * team creates an academy.
   */
  public updatePlatformSubscriptionVersionAsync(
    tenantId: string,
    platformSubscriptionVersion: AppVersion
  ): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl.account_service}/OwnerSettings/UpdatePlatformSubscriptionVersionAsync`,
      null,
      {
        params: { tenantId, platformSubscriptionVersion },
        withCredentials: true,
      }
    );
  }

  public checkAccessCdExists(cd: number): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.apiUrl.personService}/Person/IsAccessPinAlreadyInUse?accessPin=${cd}`,
      {
        withCredentials: true,
      }
    );
  }
  public searchPerson(query: string): Observable<TPersonSearch> {
    return this.http.get<TPersonSearch>(
      `${this.apiUrl.search_service}/personsearch/search`,
      {
        params: {
          q: query,
        },
        withCredentials: true,
      }
    );
  }

  public checkFiscalIdentity(
    type: 'cnpj' | 'cpf',
    value: string
  ): Observable<unknown> {
    return this.http.get<unknown>(
      `${this.apiUrl.personService}/PublicData/${type}/${value}`,
      {
        withCredentials: true,
      }
    );
  }

  public createPerson(
    person: TProfile & { OwnerAppVersion?: AppVersion }
  ): Observable<TProfile> {
    return this.http.post<TProfile>(
      `${this.apiUrl.personService}/Person`,
      person,
      {
        withCredentials: true,
      }
    );
  }

  public updatePerson(person: TProfile): Observable<TProfile> {
    return this.http.put<TProfile>(
      `${this.apiUrl.personService}/Person`,
      person,
      {
        withCredentials: true,
      }
    );
  }

  public patchPerson(propertyPatch: Partial<TProfile>): Observable<TProfile> {
    return this.http.patch<TProfile>(
      `${this.apiUrl.personService}/Person`,
      propertyPatch,
      {
        withCredentials: true,
      }
    );
  }

  public getResponsible(personId: string): Observable<TResponsible> {
    return this.http.get<TResponsible>(
      `${this.apiUrl.personService}/Responsible`,
      {
        params: { personId },
        withCredentials: true,
      }
    );
  }

  public createResponsible(
    responsible: TResponsible
  ): Observable<TResponsible> {
    return this.http.post<TResponsible>(
      `${this.apiUrl.personService}/Responsible`,
      responsible,
      {
        withCredentials: true,
      }
    );
  }

  public updateResponsible(
    responsible: TResponsible
  ): Observable<TResponsible> {
    return this.http.put<TResponsible>(
      `${this.apiUrl.personService}/Responsible`,
      responsible,
      {
        withCredentials: true,
      }
    );
  }

  public patchResponsible(
    propertyPatch: Partial<TResponsible>
  ): Observable<TResponsible> {
    return this.http.patch<TResponsible>(
      `${this.apiUrl.personService}/Responsible`,
      propertyPatch,
      {
        withCredentials: true,
      }
    );
  }

  public deleteResponsible(personId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.personService}/Responsible`, {
      params: { personId },
      withCredentials: true,
    });
  }

  public getAccessFrequency(
    personId: string,
    startOn: string,
    endOn: string
  ): Observable<{ '@odata.context': string; value: TAccessLog[] }> {
    return this.http.get<{ '@odata.context': string; value: TAccessLog[] }>(
      `${this.apiUrl.odata_access_log_service}/AccessLogs`,
      {
        params: {
          $orderby: 'AccessOnUtc',
          $select: 'AccessOnUtc,AccessLogId',
          $filter: `(AccessDirection eq ${ELogicalDirectionEnum.Entry}) and (IsAccessGranted eq true) and (AccessOnUtc ge ${startOn}) and (AccessOnUtc le ${endOn}) and (PersonId eq ${personId})`,
        },
        withCredentials: true,
      }
    );
  }

  /**
   * Fetches the most recent granted entry for the person from AccessLogs.
   */
  public getLastAccess(
    personId: string
  ): Observable<{ '@odata.context': string; value: TAccessLog[] }> {
    const endOn = DateTime.now().toISO();
    const startOn = DateTime.now().minus({ years: 10 }).toISO();
    return this.http.get<{ '@odata.context': string; value: TAccessLog[] }>(
      `${this.apiUrl.odata_access_log_service}/AccessLogs`,
      {
        params: {
          $orderby: 'AccessOnUtc desc',
          $top: '1',
          $select: 'AccessOnUtc,AccessLogId',
          $filter: `(AccessDirection eq ${ELogicalDirectionEnum.Entry}) and (IsAccessGranted eq true) and (AccessOnUtc ge ${startOn}) and (AccessOnUtc le ${endOn}) and (PersonId eq ${personId})`,
        },
        withCredentials: true,
      }
    );
  }

  public getComment(id: string): Observable<TComment> {
    return this.http.get<TComment>(`${this.apiUrl.personService}/Comment`, {
      withCredentials: true,
      params: {
        id,
      },
    });
  }

  public toggleReadComment(commentId: string): Observable<TCommentToggleRead> {
    return this.http.get<TCommentToggleRead>(
      `${this.apiUrl.personService}/Comment/ToggleRead`,
      {
        withCredentials: true,
        params: {
          commentId,
        },
      }
    );
  }

  public adicionarComentario(comment: unknown): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.apiUrl.personService}/Comment`,
      comment,
      {
        withCredentials: true,
      }
    );
  }

  public atualizarComentario(comment: unknown): Observable<unknown> {
    return this.http.put<unknown>(
      `${this.apiUrl.personService}/Comment`,
      comment,
      {
        withCredentials: true,
      }
    );
  }

  public removerComentario(id: string): Observable<unknown> {
    return this.http.delete<unknown>(`${this.apiUrl.personService}/Comment`, {
      withCredentials: true,
      params: { id },
    });
  }

  public getLastComment(
    personId: string
  ): Observable<{ '@odata.context': string; value: TComment[] }> {
    return this.http.get<{ '@odata.context': string; value: TComment[] }>(
      `${this.apiUrl.odata_person_service}/Comments`,
      {
        params: {
          targetPersonId: personId,
          $filter: 'Read eq false',
          skip: '0',
          $top: '1',
          $orderby: 'AddedOn asc',
        },
        withCredentials: true,
      }
    );
  }

  public getByPerson(personId: string): Observable<TPhone[]> {
    return this.http.get<TPhone[]>(
      `${this.apiUrl.personService}/Phone/GetByPerson`,
      {
        params: { personId },
        withCredentials: true,
      }
    );
  }

  public getPhone(id: string): Observable<TPhone> {
    return this.http.get<TPhone>(`${this.apiUrl.personService}/Phone`, {
      params: { id },
      withCredentials: true,
    });
  }

  public createPhone(phone: TPhone): Observable<TPhone> {
    return this.http.post<TPhone>(`${this.apiUrl.personService}/Phone`, phone, {
      withCredentials: true,
    });
  }

  public updatePhone(phone: TPhone): Observable<TPhone> {
    return this.http.put<TPhone>(`${this.apiUrl.personService}/Phone`, phone, {
      withCredentials: true,
    });
  }

  public patchPhone(propertyPatch: Partial<TPhone>): Observable<TPhone> {
    return this.http.patch<TPhone>(
      `${this.apiUrl.personService}/Phone`,
      propertyPatch,
      {
        withCredentials: true,
      }
    );
  }

  public deletePhone(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.personService}/Phone`, {
      params: { id },
      withCredentials: true,
    });
  }

  public getAddressByPerson(personId: string): Observable<TAddress[]> {
    return this.http.get<TAddress[]>(
      `${this.apiUrl.personService}/Address/GetByPerson`,
      {
        params: { personId },
        withCredentials: true,
      }
    );
  }

  public getAddress(id: string): Observable<TAddress> {
    return this.http.get<TAddress>(`${this.apiUrl.personService}/Address`, {
      params: { id },
      withCredentials: true,
    });
  }

  public createAddress(address: TAddress): Observable<TAddress> {
    return this.http.post<TAddress>(
      `${this.apiUrl.personService}/Address`,
      address,
      {
        withCredentials: true,
      }
    );
  }

  public updateAddress(address: TAddress): Observable<TAddress> {
    return this.http.put<TAddress>(
      `${this.apiUrl.personService}/Address`,
      address,
      {
        withCredentials: true,
      }
    );
  }

  public patchAddress(propertyPatch: Partial<TAddress>): Observable<TAddress> {
    return this.http.patch<TAddress>(
      `${this.apiUrl.personService}/Address`,
      propertyPatch,
      {
        withCredentials: true,
      }
    );
  }

  public deleteAddress(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.personService}/Address`, {
      params: { id },
      withCredentials: true,
    });
  }

  public getPersonWithIncludes(id: string): Observable<TProfile> {
    return this.http.get<TProfile>(
      `${this.apiUrl.personService}/PersonWithIncludes`,
      {
        params: { id },
        withCredentials: true,
      }
    );
  }

  public createPersonWithIncludes(person: TProfile): Observable<TProfile> {
    return this.http.post<TProfile>(
      `${this.apiUrl.personService}/PersonWithIncludes`,
      person,
      {
        withCredentials: true,
      }
    );
  }

  public updatePersonWithIncludes(person: TProfile): Observable<TProfile> {
    return this.http.put<TProfile>(
      `${this.apiUrl.personService}/PersonWithIncludes`,
      person,
      {
        withCredentials: true,
      }
    );
  }

  public patchPersonWithIncludes(
    propertyPatch: Partial<TProfile>
  ): Observable<TProfile> {
    return this.http.patch<TProfile>(
      `${this.apiUrl.personService}/PersonWithIncludes`,
      propertyPatch,
      {
        withCredentials: true,
      }
    );
  }

  public deletePersonWithIncludes(id: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl.personService}/PersonWithIncludes`,
      {
        params: { id },
        withCredentials: true,
      }
    );
  }

  public getPersonWithIncludesPost(
    params: IPersonWithIncludesParams
  ): Observable<TProfile> {
    return this.http.post<TProfile>(
      `${this.apiUrl.personService}/PersonWithIncludes/GetPersonWithIncludes`,
      params,
      { withCredentials: true }
    );
  }

  /**
   * Helper method to fetch person with specific includes
   * @param personId Person ID
   * @param includeTypes Types of data to be included in the response
   * @returns Observable with person data and requested includes
   */
  public getPersonWithSpecificIncludes(
    personId: string,
    includeTypes: EPersonIncludeType[] = []
  ): Observable<TProfile> {
    return this.getPersonWithIncludesPost({
      personId,
      includes: includeTypes,
    });
  }

  public getTotalAccountsReceivablesSummaryByPersonId(
    personId: string
  ): Observable<number> {
    return this.http.get<number>(
      `${this.apiUrl.receivable_service}/AccountReceivable/GetTotalAccountsReceivablesSummaryByPersonId`,
      {
        params: { personId },
        withCredentials: true,
      }
    );
  }

  public getTotalReceivementDailySummaryByPersonId(
    personId: string
  ): Observable<number> {
    return this.http.get<number>(
      `${this.apiUrl.receivable_service}/Receivement/GetTotalReceivementDailySummaryByPersonId`,
      {
        params: { personId },
        withCredentials: true,
      }
    );
  }

  public getTotalOpenMonthByPersonId(personId: string): Observable<number> {
    return this.http.get<number>(
      `${this.apiUrl.receivable_service}/AccountReceivable/GetTotalOpenMonthByPerson`,
      {
        params: { personId },
        withCredentials: true,
      }
    );
  }

  public getTimelineByPeriod(
    personId: string,
    yearMonth: string
  ): Observable<{ NextDate: string; List: TTimelineEvent[] }> {
    return this.http.get<{ NextDate: string; List: TTimelineEvent[] }>(
      `${this.apiUrl.personService}/Timeline`,
      {
        params: {
          personId,
          yearMonth,
        },
        withCredentials: true,
        // `Details.conditions[].neverSince` is an instant without "Utc" in its
        // name (feature 003): declared so it converts like `measuredOnUtc`.
        context: withUtcDateFields(['neverSince']),
      }
    );
  }

  public getAllGraduationsByPersonId(
    personId: string
  ): Observable<TGraduationList[]> {
    return this.http.get<TGraduationList[]>(
      `${this.apiUrl.graduation_service}/PersonActivityLevel/GetLastGraduationsByPerson`,
      {
        params: { personId },
        withCredentials: true,
      }
    );
  }

  public getGraduationById(id: string): Observable<TGraduation> {
    return this.http.get<TGraduation>(
      `${this.apiUrl.graduation_service}/PersonActivityLevel`,
      {
        params: { id },
        withCredentials: true,
      }
    );
  }

  public deleteGraduationById(id: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl.graduation_service}/PersonActivityLevel`,
      {
        params: { id },
        withCredentials: true,
      }
    );
  }

  public createGraduation(
    graduation: Omit<TGraduation, 'PersonActivityLevelId'>
  ): Observable<TGraduation> {
    return this.http.post<TGraduation>(
      `${this.apiUrl.graduation_service}/PersonActivityLevel`,
      graduation,
      {
        withCredentials: true,
      }
    );
  }

  public updateGraduation(graduation: TGraduation): Observable<TGraduation> {
    return this.http.put<TGraduation>(
      `${this.apiUrl.graduation_service}/PersonActivityLevel`,
      graduation,
      {
        withCredentials: true,
      }
    );
  }

  public getContractInfoByPersonId(personId: string): Observable<string> {
    return this.http.get(
      `${this.apiUrl.contract}/Contracts/GetInfoByPersonId?personId=${personId}`,
      {
        withCredentials: true,
        responseType: 'text',
      }
    );
  }

  public copyToPaguela(personId: string): Observable<TProfile> {
    return this.http.post<TProfile>(
      `${this.apiUrl.personService}/Person/CopyAccountActuarToPagueLa`,
      null,
      {
        params: { personId },
        withCredentials: true,
      }
    );
  }

  public generateSimpleLoginCode(personId: string): Observable<any> {
    return this.http.post(
      `${this.apiUrl.userService}/Auth/GenerateSimpleLoginCode?personId=${personId}`,
      {},
      {
        withCredentials: true,
      }
    );
  }

  public getPersonCategoryHistoryByPersonId(
    personId: string
  ): Observable<TCategoryHistory[]> {
    return this.http.get<TCategoryHistory[]>(
      `${this.apiUrl.personService}/FinancialCategory/GetHistoriesByPerson`,
      {
        params: { personId },
        withCredentials: true,
      }
    );
  }

  public getPersonCategoryByPersonId(personId: string): Observable<TCategory> {
    return this.http.get<TCategory>(
      `${this.apiUrl.personService}/FinancialCategory/GetLastByPerson`,
      {
        params: { personId },
        withCredentials: true,
      }
    );
  }

  public getCreditAssessmentPJ(cnpj: string): Observable<CreditAssessmentPJ> {
    return this.http.get<CreditAssessmentPJ>(
      `${this.apiUrl.credit_assessment_service}/SerasaPJ/GetSerasaPJ`,
      {
        params: { cnpj },
        withCredentials: true,
      }
    );
  }

  public getLastCreditAssessmentPJ(
    cnpj: string
  ): Observable<CreditAssessmentPJ> {
    return this.http.get<CreditAssessmentPJ>(
      `${this.apiUrl.credit_assessment_service}/SerasaPJ/GetSerasaPJLastAdded`,
      {
        params: { cnpj },
        withCredentials: true,
      }
    );
  }

  public getAllCreditAssessmentPJ(
    cnpj: string
  ): Observable<CreditAssessmentPJAll[]> {
    return this.http.get<CreditAssessmentPJAll[]>(
      `${this.apiUrl.credit_assessment_service}/SerasaPJ/GetSerasaPJAllAdded`,
      {
        params: { cnpj },
        withCredentials: true,
      }
    );
  }

  public getSerasaPJById(id: string): Observable<CreditAssessmentPJ> {
    return this.http.get<CreditAssessmentPJ>(
      `${this.apiUrl.credit_assessment_service}/SerasaPJ/GetSerasaPJById`,
      {
        params: { id },
        withCredentials: true,
      }
    );
  }

  public getCreditAssessmentPF(cpf: string): Observable<CreditAssessmentPF> {
    return this.http.get<CreditAssessmentPF>(
      `${this.apiUrl.credit_assessment_service}/SerasaPF/GetSerasaPF`,
      {
        params: { cpf },
        withCredentials: true,
      }
    );
  }

  public getLastCreditAssessmentPF(
    cpf: string
  ): Observable<CreditAssessmentPF> {
    return this.http.get<CreditAssessmentPF>(
      `${this.apiUrl.credit_assessment_service}/SerasaPF/GetSerasaPFLastAdded`,
      {
        params: { cpf },
        withCredentials: true,
      }
    );
  }

  public getAllCreditAssessmentPF(
    cpf: string
  ): Observable<CreditAssessmentPFAll[]> {
    return this.http.get<CreditAssessmentPFAll[]>(
      `${this.apiUrl.credit_assessment_service}/SerasaPF/GetSerasaPFAllAdded`,
      {
        params: { cpf },
        withCredentials: true,
      }
    );
  }

  public getSerasaPFById(id: string): Observable<CreditAssessmentPF> {
    return this.http.get<CreditAssessmentPF>(
      `${this.apiUrl.credit_assessment_service}/SerasaPF/GetSerasaPFById`,
      {
        params: { id },
        withCredentials: true,
      }
    );
  }

  public getIntegration(id: string): Observable<TPersonIntegration> {
    return this.http.get<TPersonIntegration>(
      `${this.apiUrl.personService}/Integration`,
      {
        params: { id },
        withCredentials: true,
      }
    );
  }

  public updateIntegration(
    data: TPersonIntegration
  ): Observable<TPersonIntegration> {
    return this.http.patch<TPersonIntegration>(
      `${this.apiUrl.personService}/Integration`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  public checkTotalPassKey(id: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl.totalpass}/Gym/VerifyCredentialAsync?gymId=${id}`,
      {
        withCredentials: true,
      }
    );
  }

  /**
   * Atividades contratadas pela pessoa, para o bloco "Atividades" do perfil.
   *
   * ⚠ 403 quando a pessoa não é do inquilino de quem chama. É leitura de fundo,
   * disparada pela abertura do perfil, e a senha de um supervisor do mesmo
   * inquilino não muda a resposta — daí o `withoutForbiddenElevation()`: o erro
   * chega à store em vez de abrir uma modal de elevação que não pode ajudar.
   */
  public getPersonProducts(personId: string): Observable<TPersonProduct[]> {
    return this.http.get<TPersonProduct[]>(
      `${this.apiUrl.subscription_service}/Person/Products/activity`,
      {
        params: {
          personId,
        },
        context: withoutForbiddenElevation(),
        withCredentials: true,
      }
    );
  }

  public getPaguelaAccount(id: string): Observable<TPaguelaAccount> {
    return this.http.get<TPaguelaAccount>(
      `${this.apiUrl.paguela}/Contas/ObterPorId`,
      {
        params: {
          id,
        },
        withCredentials: true,
      }
    );
  }

  public updatePaguelaSituation(
    id: string,
    situacao: EPaguelaSituation
  ): Observable<TPaguelaAccount> {
    return this.http.put<TPaguelaAccount>(
      `${this.apiUrl.paguela}/Contas/AtualizarSituacao`,
      null,
      {
        params: {
          id,
          situacao,
        },
        withCredentials: true,
      }
    );
  }

  public getActivitiesByPersonId(
    personId?: string
  ): Observable<{ '@odata.context': string; value: TActivity[] }> {
    return this.http.get<{ '@odata.context': string; value: TActivity[] }>(
      `${this.apiUrl.odata_activity_service}/PersonsWorkers`,
      {
        params: {
          $filter: `PersonId eq ${personId}`,
        },
        withCredentials: true,
      }
    );
  }

  public getAllActivities(): Observable<{ value: TActivity[] }> {
    return this.http.get<{ value: TActivity[] }>(
      `${this.apiUrl.odata_activity_service}/Activities`,
      {
        params: {
          $select: 'Description,ActivityId,Icon',
          $orderby: 'Description',
        },
        withCredentials: true,
      }
    );
  }

  /**
   * Active school enrollments for the person, read from the SchoolService
   * `OData/Enrollment` endpoint. Unresolved ids yield a `null` name (never an
   * error), so callers tolerate empty/partial rows. Only fired for tenants
   * with the `school` platform feature enabled.
   */
  public getSchoolEnrollmentsByPerson(
    personId: string
  ): Observable<{ '@odata.context': string; value: TSchoolEnrollment[] }> {
    return this.http.get<{
      '@odata.context': string;
      value: TSchoolEnrollment[];
    }>(`${this.apiUrl.odata_school_service}/Enrollment`, {
      params: {
        $filter: `PersonId eq ${personId} and EnrollmentStatus eq 'Active'`,
        $count: 'true',
      },
      withCredentials: true,
    });
  }
}
